// ============================================================================
// TaskAttachments.js
// ----------------------------------------------------------------------------
// Grid compacto de adjuntos con miniaturas reales, lightbox unificado y
// descarga autenticada (contrato §5 — visor de adjuntos in-app).
//
// Soporta previewType: 'image' | 'pdf' | 'office' | 'other'
// Si el backend aún no devuelve previewType/canPreview se infiere del MIME.
// ============================================================================

import React, {
  useState, useEffect, useCallback, useMemo, useRef
} from 'react';
import {
  Paperclip, Download, Trash2, Eye,
  Image as ImageIcon, FileText, File, Film,
  X, AlertCircle, ChevronLeft, ChevronRight, ZoomIn
} from 'lucide-react';
import { useFileAttachments } from './FileAttachmentsContext';
import {
  fetchThumb, fetchFile, fetchPreview,
  downloadAttachment, revokeBlobUrl
} from '../services/attachmentApi';
import '../styles/TaskAttachments.css';

// ── Helpers de tipo ───────────────────────────────────────────────────────────

// Regex del hash Notion (32 hex antes de la extensión, precedido de espacio)
const NOTION_HASH_RE = /\s+[0-9a-f]{32}(?=\.[^.]+$)/i;

function cleanName(name) {
  return name ? name.replace(NOTION_HASH_RE, '') : name;
}

function formatSize(bytes) {
  if (!bytes) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/** Tipo efectivo de preview: usa campo del backend si existe, infiere si no */
function getEffectivePreviewType(att) {
  if (att.previewType) return att.previewType;
  const mime = att.fileType || att.mimeType || '';
  if (att.isImage || mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.includes('pdf')) return 'pdf';
  if (/word|excel|powerpoint|presentation|spreadsheet|officedocument|msword|vnd\.ms-/i.test(mime))
    return 'office';
  return 'other';
}

function getEffectiveCanPreview(att) {
  if (att.canPreview !== undefined) return att.canPreview;
  return getEffectivePreviewType(att) !== 'other';
}

function getBadgeInfo(att) {
  const type = getEffectivePreviewType(att);
  const name = att.originalName || att.fileName || '';
  const ext = (name.match(/\.([^.]+)$/) || [])[1]?.toUpperCase() || '';
  switch (type) {
    case 'image':  return { label: ext || 'IMG',       color: '#f59e0b' };
    case 'video':  return { label: ext || 'MP4',       color: '#8b5cf6' };
    case 'pdf':    return { label: 'PDF',               color: '#ef4444' };
    case 'office': {
      if (/XLS|XLSX/.test(ext))      return { label: ext, color: '#10b981' };
      if (/PPT|PPTX/.test(ext))      return { label: ext, color: '#f59e0b' };
      return                                { label: ext || 'DOC', color: '#2563eb' };
    }
    default:       return { label: ext || 'FILE',       color: '#94a3b8' };
  }
}

function TypeIcon({ att, size = 20 }) {
  const type = getEffectivePreviewType(att);
  switch (type) {
    case 'image':  return <ImageIcon size={size} />;
    case 'video':  return <Film      size={size} />;
    case 'pdf':
    case 'office': return <FileText  size={size} />;
    default:       return <File      size={size} />;
  }
}

// ── AttachmentCard ────────────────────────────────────────────────────────────
// Definido fuera del componente padre para evitar remounts en cada render

function AttachmentCard({ att, thumb, onOpen, onDelete }) {
  const displayName = cleanName(att.originalName || att.fileName || 'Archivo');
  const badge = getBadgeInfo(att);

  const handleDownload = (e) => {
    e.stopPropagation();
    // Contrato §5: la descarga conserva el originalName (no el nombre limpiado)
    downloadAttachment(att.id, att.originalName || att.fileName || displayName).catch(console.error);
  };

  return (
    <div className="att-card" title={att.originalName || att.fileName}>
      {/* Miniatura + overlay de acciones */}
      <div
        className="att-thumb"
        onClick={() => onOpen(att)}
        role="button"
        tabIndex={0}
        aria-label={`Abrir ${displayName}`}
        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && onOpen(att)}
      >
        {thumb?.loading && (
          <div className="att-thumb-skeleton" />
        )}
        {thumb?.url && !thumb.loading && (
          <img src={thumb.url} alt="" draggable={false} />
        )}
        {(!thumb || (!thumb.loading && !thumb.url)) && (
          <div className="att-thumb-icon">
            <TypeIcon att={att} size={26} />
          </div>
        )}

        {/* Acciones en hover — dentro del thumb para quedar confinadas.
            Sin onClick propio: los clics fuera de un botón burbujean al thumb
            (un solo openLightbox); los botones llevan stopPropagation. */}
        <div className="att-card-actions">
          <button
            className="att-card-btn"
            onClick={e => { e.stopPropagation(); onOpen(att); }}
            title="Vista previa"
            aria-label={`Vista previa de ${displayName}`}
          >
            <Eye size={13} />
          </button>
          <button
            className="att-card-btn"
            onClick={handleDownload}
            title="Descargar"
            aria-label={`Descargar ${displayName}`}
          >
            <Download size={13} />
          </button>
          <button
            className="att-card-btn att-card-btn-del"
            onClick={e => { e.stopPropagation(); onDelete(att); }}
            title="Eliminar"
            aria-label={`Eliminar ${displayName}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="att-info">
        <div className="att-name">{displayName}</div>
        <div className="att-meta">
          <span className="att-badge" style={{ background: badge.color }}>
            {badge.label}
          </span>
          <span className="att-size">{formatSize(att.fileSize || att.size)}</span>
        </div>
      </div>
    </div>
  );
}

// ── AttachmentLightbox ────────────────────────────────────────────────────────
// Definido fuera del padre para evitar que el <iframe> se remonte en cada render

function AttachmentLightbox({
  att, blobUrl, loading, error,
  canPrev, canNext, onPrev, onNext,
  onClose, onDownload
}) {
  const [zoom, setZoom] = useState(false);
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const type = att ? getEffectivePreviewType(att) : 'other';
  const displayName = att ? cleanName(att.originalName || att.fileName || 'Archivo') : '';

  // Focus al cerrar/abrir o al cambiar de adjunto
  useEffect(() => {
    setZoom(false);
    if (closeRef.current) closeRef.current.focus();
  }, [att?.id]);

  // Trap de foco: Tab cicla dentro del modal
  const handleModalKeyDown = (e) => {
    if (e.key !== 'Tab' || !modalRef.current) return;
    const focusable = Array.from(
      modalRef.current.querySelectorAll(
        'button:not([disabled]), a, [tabindex]:not([tabindex="-1"])'
      )
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last  = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="att-lbx-state">
          <div className="att-lbx-spinner" />
          <span>{type === 'office' ? 'Generando vista previa…' : 'Cargando…'}</span>
          {type === 'office' && (
            <span className="att-lbx-hint">La primera vez puede tardar unos segundos</span>
          )}
        </div>
      );
    }

    if (error) {
      const unavailable = error === 503 || error === 415;
      return (
        <div className="att-lbx-state att-lbx-state-error">
          <AlertCircle size={30} />
          <span>
            {unavailable
              ? 'Vista previa no disponible'
              : 'No se pudo cargar la vista previa'}
          </span>
          <button className="att-lbx-action-btn" onClick={onDownload}>
            <Download size={13} />
            Descargar
          </button>
        </div>
      );
    }

    if (type === 'other' || !blobUrl) {
      return (
        <div className="att-lbx-state">
          <TypeIcon att={att} size={38} />
          <span>Sin vista previa disponible</span>
          <button className="att-lbx-action-btn" onClick={onDownload}>
            <Download size={13} />
            Descargar
          </button>
        </div>
      );
    }

    if (type === 'video') {
      return (
        <video
          src={blobUrl}
          controls
          autoPlay
          playsInline
          style={{ maxWidth: '100%', maxHeight: '100%', display: 'block', margin: '0 auto', background: '#000' }}
        />
      );
    }

    if (type === 'image') {
      return (
        <div className={`att-lbx-img-wrap${zoom ? ' att-lbx-zoomed' : ''}`}>
          <img
            src={blobUrl}
            alt={displayName}
            className="att-lbx-img"
            onClick={() => setZoom(z => !z)}
            title={zoom ? 'Clic para reducir' : 'Clic para ampliar'}
            draggable={false}
          />
        </div>
      );
    }

    // pdf | office → iframe con el PDF (nativo del navegador, sin pdf.js)
    return (
      <iframe
        key={blobUrl}
        src={blobUrl}
        className="att-lbx-iframe"
        title={`Vista previa de ${displayName}`}
      />
    );
  };

  if (!att) return null;

  return (
    <div
      className="att-lightbox-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Visor: ${displayName}`}
    >
      <div
        className="att-lightbox"
        ref={modalRef}
        onClick={e => e.stopPropagation()}
        onKeyDown={handleModalKeyDown}
      >
        {/* Cabecera */}
        <div className="att-lbx-header">
          <span className="att-lbx-title" title={att.originalName || att.fileName}>
            {displayName}
          </span>
          <span className="att-lbx-meta">
            {formatSize(att.fileSize || att.size)}
          </span>
          <button
            ref={closeRef}
            className="att-lbx-close"
            onClick={onClose}
            aria-label="Cerrar visor (Esc)"
          >
            <X size={15} />
          </button>
        </div>

        {/* Contenido */}
        <div className="att-lbx-body">
          {renderBody()}
        </div>

        {/* Pie: navegación + acciones */}
        <div className="att-lbx-footer">
          <div className="att-lbx-nav">
            <button
              className="att-lbx-nav-btn"
              onClick={onPrev}
              disabled={!canPrev}
              aria-label="Adjunto anterior (←)"
            >
              <ChevronLeft size={13} />
            </button>
            <button
              className="att-lbx-nav-btn"
              onClick={onNext}
              disabled={!canNext}
              aria-label="Adjunto siguiente (→)"
            >
              <ChevronRight size={13} />
            </button>
          </div>

          <div className="att-lbx-actions">
            {type === 'image' && blobUrl && (
              <button
                className="att-lbx-action-btn"
                onClick={() => setZoom(z => !z)}
              >
                <ZoomIn size={13} />
                {zoom ? 'Reducir' : 'Ampliar'}
              </button>
            )}
            <button className="att-lbx-action-btn" onClick={onDownload}>
              <Download size={13} />
              Descargar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── TaskAttachments (componente principal) ────────────────────────────────────

function TaskAttachments({ taskId }) {
  // ── Hooks (todos antes de cualquier early return) ──────────────────────────

  const {
    getTaskAttachments,
    fetchTaskAttachments,
    deleteAttachment,
    isLoading,
    errors
  } = useFileAttachments();

  // thumbs: { [id]: { url, loading, error } }
  const [thumbs, setThumbs] = useState({});

  // lightbox: { open, att, blobUrl, loading, error }
  const [lightbox, setLightbox] = useState({
    open: false, att: null, blobUrl: null, loading: false, error: null
  });

  // confirmación de borrado
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Refs para limpiar blobs al desmontar
  const thumbBlobsRef      = useRef({});
  const lightboxBlobRef    = useRef(null);
  const requestedThumbsRef = useRef(new Set());

  // Guard de carrera del lightbox: cada apertura/navegación incrementa el id;
  // las respuestas async que ya no corresponden se descartan y se revocan.
  const lightboxReqRef     = useRef(0);
  // Elemento que abrió el visor, para devolverle el foco al cerrar (a11y).
  const triggerElRef       = useRef(null);
  const lightboxOpenRef    = useRef(false);
  // Foco inicial del modal de borrado.
  const deleteCancelRef    = useRef(null);

  // Carga inicial + listener de subida exitosa
  useEffect(() => {
    if (!taskId) return;
    fetchTaskAttachments(taskId);

    const handleUploaded = (e) => {
      if (e.detail?.taskId === taskId) fetchTaskAttachments(taskId);
    };
    window.addEventListener('filesUploaded', handleUploaded);
    return () => window.removeEventListener('filesUploaded', handleUploaded);
  }, [taskId, fetchTaskAttachments]);

  // Cleanup al desmontar Y al cambiar de tarea (la ruta /task/detail/:taskId no
  // fuerza remount): revoca todos los blobs y resetea el estado de miniaturas
  // para no arrastrar/filtrar blobs de la tarea anterior.
  useEffect(() => {
    return () => {
      Object.values(thumbBlobsRef.current).forEach(revokeBlobUrl);
      thumbBlobsRef.current = {};
      requestedThumbsRef.current = new Set();
      if (lightboxBlobRef.current) {
        revokeBlobUrl(lightboxBlobRef.current);
        lightboxBlobRef.current = null;
      }
      setThumbs({});
    };
  }, [taskId]);

  const allAttachments = getTaskAttachments(taskId);
  const isLoadingList  = isLoading?.[taskId] || false;
  const fetchError     = errors?.[`fetch-${taskId}`] || null;

  // Carga de miniaturas para adjuntos previewables (lazy, sin duplicados, con
  // cola de concurrencia limitada para no saturar el navegador ni el backend
  // —cada thumb de Office puede forzar una conversión LibreOffice—).
  useEffect(() => {
    const THUMB_W = 256;        // nítido en pantallas 2x (cards ~128px)
    const MAX_CONCURRENT = 4;

    const pending = allAttachments.filter(
      att => !requestedThumbsRef.current.has(att.id) && getEffectiveCanPreview(att)
    );
    if (pending.length === 0) return;

    let cancelled = false;
    pending.forEach(att => {
      requestedThumbsRef.current.add(att.id);
      setThumbs(prev => ({ ...prev, [att.id]: { loading: true, url: null, error: null } }));
    });

    let idx = 0;
    const worker = async () => {
      while (!cancelled && idx < pending.length) {
        const att = pending[idx++];
        try {
          const url = await fetchThumb(att.id, THUMB_W);
          if (cancelled) { revokeBlobUrl(url); return; }
          thumbBlobsRef.current[att.id] = url;
          setThumbs(prev => ({ ...prev, [att.id]: { loading: false, url, error: null } }));
        } catch (err) {
          if (cancelled) return;
          setThumbs(prev => ({
            ...prev,
            [att.id]: { loading: false, url: null, error: err.status || err.message }
          }));
        }
      }
    };
    for (let i = 0; i < Math.min(MAX_CONCURRENT, pending.length); i++) worker();

    return () => { cancelled = true; };
  }, [allAttachments]);

  // Adjuntos previewables (para navegación del lightbox)
  const previewableAtts = useMemo(
    () => allAttachments.filter(getEffectiveCanPreview),
    [allAttachments]
  );

  const currentPreviewIdx = lightbox.open && lightbox.att
    ? previewableAtts.findIndex(a => a.id === lightbox.att.id)
    : -1;

  // ── Abrir lightbox ─────────────────────────────────────────────────────────

  const openLightbox = useCallback(async (att) => {
    // Guardar el disparador para devolverle el foco al cerrar (solo al abrir desde cerrado)
    if (!lightboxOpenRef.current) {
      triggerElRef.current = document.activeElement;
    }
    lightboxOpenRef.current = true;

    // Liberar blob anterior e invalidar cualquier carga en vuelo
    if (lightboxBlobRef.current) {
      revokeBlobUrl(lightboxBlobRef.current);
      lightboxBlobRef.current = null;
    }
    const reqId = ++lightboxReqRef.current;

    const type = getEffectivePreviewType(att);

    if (type === 'other') {
      setLightbox({ open: true, att, blobUrl: null, loading: false, error: null });
      return;
    }

    setLightbox({ open: true, att, blobUrl: null, loading: true, error: null });

    try {
      const url = (type === 'image' || type === 'video')
        ? await fetchFile(att.id)
        : await fetchPreview(att.id);
      // Si llegó otra apertura/navegación/cierre mientras tanto, descartar y revocar
      if (reqId !== lightboxReqRef.current) {
        revokeBlobUrl(url);
        return;
      }
      lightboxBlobRef.current = url;
      setLightbox(prev => ({ ...prev, blobUrl: url, loading: false }));
    } catch (err) {
      if (reqId !== lightboxReqRef.current) return;
      setLightbox(prev => ({
        ...prev, loading: false,
        error: err.status || err.message || 'Error'
      }));
    }
  }, []);

  const closeLightbox = useCallback(() => {
    lightboxOpenRef.current = false;
    lightboxReqRef.current++;            // invalida cualquier carga en vuelo
    if (lightboxBlobRef.current) {
      revokeBlobUrl(lightboxBlobRef.current);
      lightboxBlobRef.current = null;
    }
    setLightbox({ open: false, att: null, blobUrl: null, loading: false, error: null });
    // Restaurar el foco al elemento que abrió el visor
    const trigger = triggerElRef.current;
    triggerElRef.current = null;
    if (trigger && typeof trigger.focus === 'function') {
      setTimeout(() => trigger.focus(), 0);
    }
  }, []);

  const navigateLightbox = useCallback((dir) => {
    if (currentPreviewIdx < 0) return;     // el adjunto abierto no es previsualizable
    const nextIdx = currentPreviewIdx + dir;
    if (nextIdx < 0 || nextIdx >= previewableAtts.length) return;
    openLightbox(previewableAtts[nextIdx]);
  }, [currentPreviewIdx, previewableAtts, openLightbox]);

  // Teclado: Esc cierra, flechas navegan
  useEffect(() => {
    if (!lightbox.open) return;
    const handle = (e) => {
      if (e.key === 'Escape') { closeLightbox(); return; }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault(); navigateLightbox(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault(); navigateLightbox(-1);
      }
    };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [lightbox.open, closeLightbox, navigateLightbox]);

  // Bloquear el scroll del fondo mientras el visor está abierto
  useEffect(() => {
    if (!lightbox.open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [lightbox.open]);

  // a11y del modal de borrado: Esc cierra y el foco inicial entra en "Cancelar"
  useEffect(() => {
    if (!deleteConfirm) return;
    deleteCancelRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') setDeleteConfirm(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [deleteConfirm]);

  // ── Eliminar adjunto ───────────────────────────────────────────────────────

  const handleDelete = useCallback(async (att) => {
    if (!deleteAttachment) return;
    const result = await deleteAttachment(taskId, att.id).catch(() => ({ success: false }));
    if (result.success) {
      setDeleteConfirm(null);
      // Liberar blob de miniatura si existe
      if (thumbBlobsRef.current[att.id]) {
        revokeBlobUrl(thumbBlobsRef.current[att.id]);
        delete thumbBlobsRef.current[att.id];
      }
      requestedThumbsRef.current.delete(att.id);
    }
  }, [deleteAttachment, taskId]);

  // ── Early return (después de todos los hooks) ──────────────────────────────

  if (!taskId) return null;

  // ── Render ─────────────────────────────────────────────────────────────────

  const showSkeleton = isLoadingList && allAttachments.length === 0;

  return (
    <div className="task-attachments">
      {/* Cabecera */}
      <div className="attachments-header">
        <h3 className="attachments-title">
          <Paperclip className="section-icon" />
          Archivos adjuntos
          {allAttachments.length > 0 && (
            <span className="attachments-count">{allAttachments.length}</span>
          )}
        </h3>
      </div>

      {/* Cuerpo */}
      {showSkeleton ? (
        <div className="att-grid">
          {[1, 2, 3, 4].map(n => (
            <div key={n} className="att-card">
              <div className="att-thumb"><div className="att-thumb-skeleton" /></div>
              <div className="att-info">
                <div className="att-skel-line att-skel-wide" />
                <div className="att-skel-line att-skel-narrow" />
              </div>
            </div>
          ))}
        </div>
      ) : fetchError ? (
        <div className="attachments-error">
          <AlertCircle className="error-icon" />
          <span>Error al cargar los archivos adjuntos</span>
          <button
            className="retry-button"
            onClick={() => fetchTaskAttachments(taskId)}
          >
            Reintentar
          </button>
        </div>
      ) : allAttachments.length === 0 ? (
        <div className="attachments-empty">
          <Paperclip className="empty-icon" />
          <p>Sin archivos adjuntos</p>
          <span>Usa "Subir archivos" para agregar documentos</span>
        </div>
      ) : (
        <div className="att-grid">
          {allAttachments.map(att => (
            <AttachmentCard
              key={att.id}
              att={att}
              thumb={thumbs[att.id]}
              onOpen={openLightbox}
              onDelete={setDeleteConfirm}
            />
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox.open && (
        <AttachmentLightbox
          att={lightbox.att}
          blobUrl={lightbox.blobUrl}
          loading={lightbox.loading}
          error={lightbox.error}
          canPrev={currentPreviewIdx > 0}
          canNext={currentPreviewIdx >= 0 && currentPreviewIdx < previewableAtts.length - 1}
          onPrev={() => navigateLightbox(-1)}
          onNext={() => navigateLightbox(1)}
          onClose={closeLightbox}
          onDownload={() => {
            if (!lightbox.att) return;
            // Contrato §5: descarga con el originalName, no el nombre limpiado
            const fname = lightbox.att.originalName || lightbox.att.fileName || 'archivo';
            downloadAttachment(lightbox.att.id, fname).catch(console.error);
          }}
        />
      )}

      {/* Modal de confirmación de borrado */}
      {deleteConfirm && (
        <div className="delete-overlay" onClick={() => setDeleteConfirm(null)}>
          <div
            className="delete-modal"
            onClick={e => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Confirmar eliminación"
            onKeyDown={(e) => {
              if (e.key !== 'Tab') return;
              const f = Array.from(e.currentTarget.querySelectorAll('button:not([disabled])'));
              if (!f.length) return;
              const first = f[0], last = f[f.length - 1];
              if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
              else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
            }}
          >
            <div className="delete-header">
              <AlertCircle className="delete-icon" />
              <h3>Confirmar eliminación</h3>
            </div>
            <div className="delete-content">
              <p>¿Eliminar este archivo?</p>
              <div className="delete-file-info">
                <TypeIcon att={deleteConfirm} size={18} />
                <span title={deleteConfirm.originalName}>
                  {cleanName(deleteConfirm.originalName || deleteConfirm.fileName || 'Archivo')}
                </span>
              </div>
              <p className="delete-warning">Esta acción no se puede deshacer.</p>
            </div>
            <div className="delete-actions">
              <button
                ref={deleteCancelRef}
                className="delete-action cancel"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancelar
              </button>
              <button
                className="delete-action confirm"
                onClick={() => handleDelete(deleteConfirm)}
              >
                <Trash2 size={15} />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskAttachments;
